import { Component, h, Element, Prop, State, Event, EventEmitter, Method } from "@stencil/core"
import Handlebars from "handlebars"
import { Carousel } from "flowbite"
import type { CarouselItem, CarouselOptions, CarouselInterface } from "flowbite"
import type { InstanceOptions } from "flowbite"
import { getBotTemplate, getTemplate } from "../../../../utils/utils"
import { setItemListElement } from "../../../../utils/global-store"

@Component({
  tag: "item-list",
  styleUrls: ["item-list.css", "../../../../global/tailwind.min.css"],
  shadow: true,
})
export class ItemList {
  @Element() private element: HTMLElement

  @Prop() inputType: string = "ai"
  @Prop() carouselType: string
  @Prop() customTemplate = []
  @Prop() customSource

  @State() currentPage: number = 1
  @State() htmlTemplate
  @State() currentIndex: number = 0
  @State() formSettings: any
  @State() itemsPerPage: number = 10
  @State() emptyMessage: string = "No items found"
  @State() buttonText: string = "Details"
  @State() panelTemplate: any
  @State() panelIndex: number = 0
  @State() panelName: string = ""
  @State() items = []
  @State() theme: any
  @Event() sendQueryToAI: EventEmitter

  private scripts = ["https://cdn.jsdelivr.net/npm/flowbite@2.5.2/dist/flowbite.min.js"]
  private styles = ["https://cdn.jsdelivr.net/npm/flowbite@2.5.2/dist/flowbite.min.css"]

  componentWillLoad() {
    setItemListElement(this.element)
    if (this.customSource) {
      this.itemsPerPage = this.carouselType === "custom" ? 10 : this.customSource.carousel.itemsPerPage ? this.customSource.carousel.itemsPerPage : 10
      this.buttonText = this.customSource.carousel.buttonText
      this.emptyMessage = this.customSource.carousel.emptyMessage
      this.formSettings = this.customSource.formSettings
      this.items = this.customSource.items
      this.theme = this.customSource.theme
    }
    this.loadScripts()
    this.loadStyles()
    this.updateItems()
    this.renderTemplate()
  }

  @Method()
  async selectByIndex(index: number) {
    // console.log("selectByIndex", index)
    this.panelIndex = index
    this.panelTemplate = this.customTemplate[index].content
  }

  @Method()
  async selectByName(name: string) {
    this.panelName = name
    this.panelIndex = this.customTemplate.findIndex(item => item.name === name)
    this.panelTemplate = this.customTemplate.find(item => item.name === name)?.content
  }

  updateItems() {
    this.items = this.items.map(
      item =>
        item.hideImageOnMore === undefined
          ? { ...item, hideImageOnMore: true } // Update the specific property
          : item // Keep the rest unchanged
    )
  }
  loadScripts() {
    this.scripts.forEach(script => {
      const scriptElement = document.createElement("script")
      scriptElement.src = script // Path relative to the www root
      scriptElement.onload = () => {
        // console.log("Script loaded")
      }
      scriptElement.onerror = err => {
        console.error("Failed to load script:", err)
      }
      document.head.appendChild(scriptElement)
      this.element.shadowRoot.appendChild(scriptElement)
    })
  }

  loadStyles() {
    this.styles.forEach(style => {
      const styleElement = document.createElement("link")
      styleElement.rel = "stylesheet" // Path relative to the www root
      styleElement.href = style
      styleElement.onload = () => {
        // console.log("Style loaded")
      }

      styleElement.onerror = err => {
        console.error("Failed to load style:", err)
      }
      document.head.appendChild(styleElement)
      this.element.shadowRoot.appendChild(styleElement)
      // this.loadModal()
    })
  }

  getCurrentPageItems() {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage
    const endIndex = startIndex + this.itemsPerPage
    return this.items.slice(startIndex, endIndex)
  }

  totalPages() {
    return Math.ceil(this.items.length / this.itemsPerPage)
  }

  addEventListeners() {
    this.element.shadowRoot.querySelectorAll(".read-more").forEach(readMore => {
      readMore.addEventListener("click", event => {
        // const index = (event.target as HTMLElement).getAttribute("data-index") // Get index from data-index attribute
        const card = (event.target as HTMLElement).closest(".carousel-card")
        const shortDescription = card?.querySelector(".short-description")
        const longDescription = card?.querySelector(".long-description")
        const cardImage = card?.querySelector(".card-image")
        const cardRightDiv = card?.querySelector(".card-right")

        // Toggle visibility of descriptions
        shortDescription.classList.toggle("hidden")
        longDescription.classList.toggle("hidden")

        // Update button text
        if (shortDescription.classList.contains("hidden")) {
          cardImage.classList.add("hidden")
          cardRightDiv.classList.add("w-full")
          cardRightDiv.classList.remove("md:w-2/3")
          ;(event.target as HTMLElement).textContent = "Show Less"
        } else {
          cardImage.classList.remove("hidden")
          cardRightDiv.classList.remove("w-full")
          cardRightDiv.classList.add("md:w-2/3")
          ;(event.target as HTMLElement).textContent = "Read More"
        }

        // Optionally, you can use the index to log or perform other actions
        // console.log(`Button clicked for card at index: ${index}`)
      })
    })
    this.element.shadowRoot.querySelectorAll(".card-button").forEach(button => {
      button.addEventListener("click", event => {
        // Register a Handlebars helper to stringify objects
        Handlebars.registerHelper("json", function (context) {
          return JSON.stringify(context)
        })
        const id = (event.target as HTMLElement).getAttribute("data-id") // Get custom data-id attribute
        // console.log(`Button clicked for item with ID: ${id}`)

        // Add your logic here
        const type = (event.target as HTMLElement).getAttribute("data-type") // Get custom data-id attribute
        // console.log(`Button clicked for item with ID: ${type}`)

        const url = (event.target as HTMLElement).getAttribute("data-url")
        // console.log(`Button clicked for item with ID: ${url}`)
        // Add your logic here
        // let value = (event.target as HTMLElement).getAttribute("data-item")
        // // console.log(`Button clicked for item with ID: ${value}`)
        // if (value) {
        //   value = value.replace(/&quot;/g, '"')
        // }
        // const itemObject = JSON.parse(value)
        // console.log("itemObject", itemObject)
        const clickHandler = (event.target as HTMLElement).getAttribute("onClick")
        // console.log("clickHandler", clickHandler)
        if (type === "panel") {
          Handlebars.registerHelper("inc", function (value) {
            return parseInt(value) + 1
          })
          const templateResult = getTemplate("list")[1]
          if (this.carouselType !== "custom") {
            this.panelTemplate = typeof templateResult === "string" ? templateResult : templateResult.content
          } else if (this.carouselType === "custom" && !clickHandler) {
            this.panelIndex = Number(id) + 1
            this.selectByIndex(Number(id) + 1)
          }
          const cardList = this.element.shadowRoot.querySelectorAll(".carousel-card")
          const card = (event.target as HTMLElement).closest(".carousel-card")
          const cardIndex = card.getAttribute("data-index")
          const compiledPanelTemplate = Handlebars.compile(this.panelTemplate)
          // console.log("compiledPanelTemplate", panelTemplate)
          cardList[cardIndex].innerHTML = compiledPanelTemplate({
            form: this.items[cardIndex].panels[Number(id)]?.form,
            formSettings: this.formSettings,
            buttons: this.items[cardIndex].panels[Number(id)]?.buttons,
            theme: this.theme,
            item: this.items[cardIndex],
            isBot: this.inputType === "bot" ? true : false,
          })

          setTimeout(() => {
            this.addEventForPanel()
          }, 1000)
        }
        if (type === "url") {
          window.open(url, "_blank")
        }
      })
    })
  }

  addEventForPanel() {
    this.element.shadowRoot.querySelectorAll(".panel-button").forEach(button => {
      button.addEventListener("click", event => {
        const id = (event.target as HTMLElement).getAttribute("data-id") // Get custom data-id attribute
        // console.log(`Button clicked for item with ID: ${id}`)

        // Add your logic here
        const type = (event.target as HTMLElement).getAttribute("data-type") // Get custom data-id attribute
        // console.log(`Button clicked for item with ID: ${type}`)

        const clickHandler = (event.target as HTMLElement).getAttribute("onClick")
        // console.log("clickHandler", clickHandler)
        if (!clickHandler) {
          this.panelIndex = Number(id)
        }

        // Add your logic here
        if (type === "panel" && this.panelIndex === 0) {
          this.updateContent()
        }
        if (type === "panel" && this.panelIndex !== 0 && this.carouselType === "custom") {
          const cardList = this.element.shadowRoot.querySelectorAll(".carousel-card")
          const card = (event.target as HTMLElement).closest(".carousel-card")
          const cardIndex = card.getAttribute("data-index")
          // console.log("cardIndex", cardIndex)

          const compiledPanelTemplate = Handlebars.compile(this.panelTemplate)
          // console.log("compiledPanelTemplate", panelTemplate)
          cardList[cardIndex].innerHTML = compiledPanelTemplate({
            form: this.items[cardIndex].panels[Number(id)]?.form,
            formSettings: this.formSettings,
            buttons: this.items[cardIndex].panels[Number(id)]?.buttons,
            theme: this.theme,
            item: this.items[cardIndex],
            isBot: this.inputType === "bot" ? true : false,
          })

          setTimeout(() => {
            this.addEventForPanel()
            // this.addEventListeners()
          }, 1000)
        }
      })
    })
  }

  loadList() {
    const carouselList = {
      // itemsPerPage: this.itemsPerPage,
      // items: this.items,
      // totalItems: this.items.length,
      // totalPages: Math.ceil(this.items.length / this.itemsPerPage),

      // Handle Next
      next: () => {
        if (this.currentPage < this.totalPages()) {
          this.currentPage++
        }
        this.updateContent()
        this.loadImageCarousel()
      },

      // Handle Prev
      prev: () => {
        if (this.currentPage > 1) {
          this.currentPage--
        }
        this.updateContent()
        this.loadImageCarousel()
      },
      totalPages: () => {
        return Math.ceil(this.items.length / this.customSource.carousel?.itemsPerPage)
      },
    }
    this.element.shadowRoot.querySelector(".prev-btn")?.addEventListener("click", () => {
      carouselList.prev()
    })
    this.element.shadowRoot.querySelector(".next-btn")?.addEventListener("click", () => {
      carouselList.next()
    })
  }

  loadImageCarousel() {
    const pageItems = this.getCurrentPageItems()
    if (pageItems.length > 0) {
      const carouselContainers = this.element.shadowRoot.querySelectorAll(".carousel-container")
      carouselContainers.forEach((container, idx) => {
        const carouselId = container.getAttribute("data-carousel-id")
        const carouselElement = this.element.shadowRoot.querySelector(`#${carouselId}`)

        if (carouselElement) {
          // Select all images within the carousel
          const imageElement = carouselElement.querySelectorAll("[data-carousel-item]")
          const imageList: CarouselItem[] = []
          const imageIndicators: { position: number; el: HTMLElement }[] = []
          imageElement.forEach((_image, index) => {
            imageList.push({ position: index, el: this.element.shadowRoot.querySelector(`#carousel-${idx}-item-${index}`) })
            if (imageElement.length <= 3 && imageElement.length > 1) {
              imageIndicators.push({
                position: index,
                el: this.element.shadowRoot.querySelector(`#carousel-${idx}-indicator-${index}`),
              })
            }
          })

          const options: CarouselOptions = {
            defaultPosition: 1,
            interval: 3000,

            indicators: {
              activeClasses: "bg-gray-800 indicator-${idx}",
              inactiveClasses: "bg-blue-500 indicator-${idx}",
              items: imageIndicators,
            },

            // callback functions
            onNext: () => {
              // console.log("next slider item is shown")
            },
            onPrev: () => {
              // console.log("previous slider item is shown")
            },
            onChange: () => {
              // console.log("new slider item has been shown")
            },
          }

          // Create a new instance of the Flowbite Carousel
          const instanceOptions: InstanceOptions = {
            id: `#${carouselId}`,
            override: true,
          }

          const imageCarousel = new Carousel(carouselElement as HTMLElement, imageList, options, instanceOptions)

          const $prevButton = this.element.shadowRoot.querySelector(`#carousel-${idx}-prev`)
          const $nextButton = this.element.shadowRoot.querySelector(`#carousel-${idx}-next`)

          $prevButton?.addEventListener("click", () => {
            imageCarousel.prev()
          })

          $nextButton?.addEventListener("click", () => {
            imageCarousel.next()
          })

          // Start the carousel cycle
          // imageCarousel.cycle()
        }
      })
    }
  }

  loadCarousel() {
    let carouselList: CarouselItem[] = []
    let carouselIndicators: { position: number; el: HTMLElement }[] = []
    const carouselElement: HTMLElement = this.element.shadowRoot.querySelector("#carouselExample")
    const carouselItems = this.element.shadowRoot.querySelectorAll("div[data-carousel-item]")

    // Ensure the carouselItems array is not empty
    if (carouselItems.length > 0) {
      carouselItems.forEach((_item, index) => {
        carouselList.push({
          position: index,
          el: this.element.shadowRoot.querySelector(`#carousel-item-${index}`),
        })
        if (carouselItems.length <= 5) {
          carouselIndicators.push({
            position: index,
            el: this.element.shadowRoot.querySelector(`#carousel-indicator-${index}`),
          })
        }
      })
    } else {
      console.error("No carousel items found in the DOM")
    }

    // object options with default values
    const options: CarouselOptions = {
      defaultPosition: 1,
      interval: 3000,

      indicators: {
        activeClasses: "bg-gray-800",
        inactiveClasses: "bg-blue-500",
        items: carouselIndicators,
      },

      // callback functions
      onNext: () => {
        console.log("next slider item is shown")
      },
      onPrev: () => {
        console.log("previous slider item is shown")
      },
      onChange: () => {
        console.log("new slider item has been shown")
      },
    }

    // instance options object
    const instanceOptions: InstanceOptions = {
      id: "carouselExample",
      override: true,
    }

    const carousel: CarouselInterface = new Carousel(carouselElement, carouselList, options, instanceOptions)
    const $prevButton = this.element.shadowRoot.querySelector("#data-carousel-prev")
    const $nextButton = this.element.shadowRoot.querySelector("#data-carousel-next")

    $prevButton?.addEventListener("click", () => {
      carousel.prev()
    })

    $nextButton?.addEventListener("click", () => {
      carousel.next()
    })
  }

  renderTemplate() {
    Handlebars.registerHelper("lte", function (a, b) {
      return a <= b
    })
    Handlebars.registerHelper("gte", function (a, b) {
      return a > b
    })
    Handlebars.registerHelper("and", function (v1, v2, options) {
      if (v1 && v2) {
        return options.fn(this) // Only render if both variables are truthy
      }
    })
    const currentPageItems = this.getCurrentPageItems()
    let template = "",
      templateResult
    let compiledTemplate: Handlebars.TemplateDelegate

    // Register Handlebars helper
    Handlebars.registerHelper("getPageCount", () => {
      return this.totalPages()
    })
    Handlebars.registerHelper("getcurrentPage", () => {
      return this.currentPage
    })

    switch (this.carouselType) {
      case "list":
        templateResult = this.inputType === "ai" ? getTemplate(this.carouselType) : getBotTemplate(this.carouselType)

        template = Array.isArray(templateResult) ? templateResult[0].content : templateResult

        compiledTemplate = Handlebars.compile(template)
        this.htmlTemplate = compiledTemplate({
          items: currentPageItems,
          hasPrev: this.currentPage > 1,
          hasNext: (this.currentPage - 1) * this.itemsPerPage + this.itemsPerPage < this.items.length,
          buttonText: this.buttonText,
          formSettings: this.formSettings,
          theme: this.theme,
        })
        setTimeout(() => {
          this.loadList()
          this.loadImageCarousel()
          this.addEventListeners()
        }, 1000)
        break
      case "slider":
        templateResult = getTemplate(this.carouselType)
        template = Array.isArray(templateResult) ? templateResult[0].content : templateResult
        compiledTemplate = Handlebars.compile(template)
        this.htmlTemplate = compiledTemplate({ items: this.items })
        setTimeout(() => {
          this.loadCarousel()
          this.loadImageCarousel()
          this.addEventListeners()
        }, 1000)
        break
      case "custom":
        // Register the helper
        Handlebars.registerHelper("processArray", function (array) {
          if (!Array.isArray(array)) {
            return "" // Return an empty string if the input is not an array
          }
          return new Handlebars.SafeString(JSON.stringify(array))
        })
        Handlebars.registerHelper("json", function (context) {
          return JSON.stringify(context)
            .replace(/'/g, "&apos;") // optional: escape single quotes
            .replace(/"/g, "&quot;") // escape double quotes for safe HTML
        })
        template = this.customTemplate[0].content
        compiledTemplate = Handlebars.compile(template)
        this.htmlTemplate = compiledTemplate({
          items: currentPageItems,
          hasPrev: this.currentPage > 1,
          hasNext: (this.currentPage - 1) * this.itemsPerPage + this.itemsPerPage < this.items.length,
          buttonText: this.buttonText,
          currentPage: this.currentPage,
          totalPages: this.totalPages(),
          formSettings: this.formSettings,
          isBot: this.inputType === "bot" ? true : false,
        })
        setTimeout(() => {
          this.loadList()
          this.loadCarousel()
          this.loadImageCarousel()
          this.addEventListeners()
        }, 1000)
        break
    }

    return this.htmlTemplate
  }

  updateContent() {
    const container = this.element.shadowRoot.querySelector("#template-container")
    if (container) {
      container.innerHTML = this.renderTemplate() // Re-render template
    }
  }

  render() {
    // const totalPages = this.totalPages()

    if (!this.items || this.items.length === 0) {
      return <div>{this.emptyMessage}</div>
    }
    return <div id="template-container" innerHTML={this.htmlTemplate}></div>
  }
}
